import { HoverIntent } from "./hover-intent.js";

export class Menu {
    private readonly mobileMenuTrigger = document.getElementsByClassName("header_mobile_menu")[0];
    private readonly topMenu = document.getElementsByClassName("top_menu")[0];
    private readonly topLevel: HTMLUListElement;
    private readonly mobileClose = document.getElementsByClassName("mobile_close")[0];
    private readonly html = document.getElementsByTagName("html")[0];

    public static Init(): void {
        new Menu();
    }

    constructor() {
        if (this.mobileMenuTrigger) { // Menu is not shown on the checkout pages.
            this.topLevel = this.topMenu.querySelector(":scope > ul");
            this.wireUpCountrySwitcher();
            this.wireUpTopMenuEvents();
            this.wireUpMobileEvents();
        }
    }

    private wireUpMobileEvents(): void {
        this.mobileMenuTrigger.addEventListener("click", () => this.openMobileMenu());
        this.mobileClose.addEventListener("click", (e) => this.closeMobileMenu(e));
        // We only have the top category menu at startup but still need to capture the top level clicks.
        this.wireUpMobileMenuEvents(this.topMenu);
    }

    private openMobileMenu(): void {
        this.topMenu.classList.add("active");
        this.mobileMenuTrigger.classList.add("active");
        this.html.classList.add("off_canvas");
    }

    private closeMobileMenu(e?: Event): void {
        this.topMenu.classList.remove("active");
        this.topLevel.classList.remove("active");

        // Remove active class from all child elements of the menu in one go.
        let activeElements = this.topLevel.querySelectorAll(".active");
        activeElements.forEach(element => {
            element.classList.remove("active");
        });

        this.mobileMenuTrigger.classList.remove("active");
        this.html.classList.remove("off_canvas");
        e?.preventDefault(); // Optional event parameter, can be triggered from code.
    }

    private onMobileSubNavTriggerPressed(e: Event): void {
        let el = e.target as Element;
        el.closest("div").classList.add("hide_action");
        el.parentElement.classList.add("active");
        el.parentElement.parentElement.classList.add("active");
    }

    private onMobileBackMenuPressed(e: Event): void {
        let el = e.target as Element;
        el.closest("div.hide_action").classList.remove("hide_action");
        el.closest("ul.active").classList.remove("active");
        el.closest("li.active").classList.remove("active");
    }

    private wireUpCountrySwitcher(): void {
        let switchLinks = <NodeListOf<HTMLAnchorElement>>document.querySelectorAll(".switch-currency");

        for (const switchLink of switchLinks) {
            switchLink.addEventListener("click", (e) => this.onCountrySwitcherClicked(e));
        }
    }

    private onCountrySwitcherClicked(e: MouseEvent): void {
        e.preventDefault();
        let clickedElement = <HTMLAnchorElement>e.currentTarget;
        let dropdown = <HTMLDivElement>clickedElement.nextElementSibling;

        dropdown.classList.toggle("hidden");
    }

    private wireUpTopMenuEvents(): void {
        let config = { timeout: 200 };

        // Look for top level items that have a navigation trigger so that we avoid 'brands' which does not have content to load.
        const triggerElements = document.querySelectorAll(".top_menu>ul>li span.subnav_trigger") as NodeListOf<HTMLSpanElement>;

        for (const spanElement of triggerElements) {
            // Link the hover to the parent li.
            const listElement = spanElement.parentElement as HTMLLIElement;

            // Wire up a delayed mouse over for top menu items in the desktop browser environment.
            new HoverIntent(listElement,
                () => this.loadOrShowCategoryMenu(listElement),
                () => this.makeMenuElementInactive(listElement))
                .configure(config);

            // Plus listen for clicks that will come from mobile browsers as they don't have mouse over.
            listElement.addEventListener("click", () => this.loadOrShowCategoryMenu(listElement));
        }
    }

    /**
     * Wire up menu hover events for the newly loaded child menu elements of the passed top level category element.
     * This works only for the desktop environment where mouse events exist.
     * 
     * Listens on menu items for a delayed mouse over and when fired, shows the categories below that level.
     * 
     * @param contentDiv - The top level category parent container that the new menu list elements have just been loaded into.
     */
    private wireUpSubMenuEvents(contentDiv: HTMLDivElement): void {
        const listElements = contentDiv.querySelectorAll("ul>li") as NodeListOf<HTMLLIElement>;

        for (let listElement of listElements) {
            // Wire up a delayed mouse over for sub menu items in the desktop browser environment.
            new HoverIntent(listElement,
                () => this.makeMenuElementActive(listElement),
                () => this.makeMenuElementInactive(listElement));
        }
    }

    /**
     * Wire up click events for the child menu elements of the passed element.
     * This is just for the mobile envoronment as we don't get mouse events there.
     *
     * @param parentElement - The parent container that the menu list elements exist in.
     *      This will be the top level on page load and the second level when dynamic.
     */
    private wireUpMobileMenuEvents(parentElement: Element): void {
        const subnavTrigger = parentElement.getElementsByClassName("subnav_trigger");
        for (const subnav of subnavTrigger) {
            subnav.addEventListener("click", (e) => this.onMobileSubNavTriggerPressed(e));
        }

        const backMenu = parentElement.getElementsByClassName("back");
        for (const subnav of backMenu) {
            subnav.addEventListener("click", (e) => this.onMobileBackMenuPressed(e));
        }

        const closeMenu = parentElement.getElementsByClassName("close");
        for (const subnav of closeMenu) {
            subnav.addEventListener("click", (e) => this.closeMobileMenu(e));
        }
    }

    /**
     * Based on the link contained within the anchor in the passed list element,
     * will load the menu structure for a top level category from the server.
     * 
     * If the menu has already been loaded (or is loading) then will just show it.
     * @param listElement
     */
    private loadOrShowCategoryMenu(listElement: HTMLLIElement): void {
        if (!listElement.dataset.alreadyFetched) {
            // We only fetch once per menu, even if it fails.
            listElement.dataset.alreadyFetched = "true";

            // Extract the URL for the category we moused over.
            let menuLink = listElement.querySelector("a");
            let menuUrl = this.convertCategoryToMenuUrl(menuLink);

            // Level 2 already exists on page load so make that visible as a failover in case the replacement does not load.
            this.makeMenuElementActive(listElement);

            fetch(menuUrl)
                .then(response => {
                    if (response.ok) {
                        return response.text();
                    } else {
                        // Menu failed to load. If this was mobile then the UI will be blocked as the close button
                        // won't have loaded. The only thing we can do is close the menu. It's annoying for the customer.
                        this.closeMobileMenu(null);
                        // Which is why we throw an error that won't be handled here (caught by our client side logging).
                        throw new Error(`Failed to fetch category menu for url '${menuUrl}'. Status: ${response.status} / ${response.statusText}`);
                    }
                })
                .then(html => {
                    // Remove existing div as it'll contain categories with links but no sub-categories.
                    let previousDiv = listElement.querySelector("div");
                    let previousDivActive = true;

                    if (previousDiv) {
                        // We should copy the state of the level 2 div as it might have been hidden in the time it takes to load from the server.
                        previousDivActive = previousDiv.classList.contains("active");
                        previousDiv.remove();
                    }

                    listElement.insertAdjacentHTML('beforeend', html);

                    let div: HTMLDivElement;
                    if (previousDivActive) {
                        div = this.makeMenuElementActive(listElement);
                    }
                    else {
                        div = this.makeMenuElementInactive(listElement);
                    }

                    // We've just inserted a level 2 menu which may have level 3 and 4 items so need to add mouseover for it as well.
                    this.wireUpSubMenuEvents(div);
                    this.wireUpMobileMenuEvents(div);
                });
        }
        else {
            // Div may already exist, just make it active to show it again.
            listElement.querySelector("div")?.classList.add("active");
        }
    }

    /**
     * Turns a category URL into one from which menu HTML by dynamically retrieved.
     * @param menuLink The anchor tag to extract the category URL from.
     * @returns A url of the format /category1.menu or /fr/category1.menu.
     */
    private convertCategoryToMenuUrl(menuLink: HTMLAnchorElement): string {
        // Extract the URL from the anchor tag.
        // This will return us an absolute URL such as https://site.com/category1{.cat}
        const absoluteCategoryUrl = new URL(menuLink.href);

        // Turn this URL into a relative one so it becomes /category1{.cat}
        const relativeCategoryUrl = absoluteCategoryUrl.pathname;

        // Remove the extension from the URL if it exists (.cat, some sites do not have extensions).
        const extensionIndex = relativeCategoryUrl.lastIndexOf(".");

        // Strip off the extension if it exists.
        const urlWithoutExtension = extensionIndex !== -1 ? relativeCategoryUrl.substring(0, extensionIndex) : relativeCategoryUrl;

        // Return the URL with a .menu extension so it will route directly.
        // This handles the case where we are on a locale such as /fr/category1.menu as /fr will get stripped before the route is chosen.
        return urlWithoutExtension + ".menu";
    }

    private makeMenuElementActive(listElement: Element): HTMLDivElement {
        let div = listElement.querySelector("div");

        if (!this.isMobileMenuOpen()) {
            listElement.classList.add("active");

            if (div) {
                div.classList.add("active");
            }
        }

        // Mobile makes use of this too after adding content.
        return div;
    }

    private makeMenuElementInactive(listElement: HTMLLIElement): HTMLDivElement {
        let div = listElement.querySelector("div");

        if (!this.isMobileMenuOpen()) {
            listElement.classList.remove("active");

            if (div) {
                div.classList.remove("active");
            }
        }

        return div;
    }

    private isMobileMenuOpen(): boolean {
        return this.mobileMenuTrigger.classList.contains("active");
    }
}

document.addEventListener("DOMContentLoaded", () => { Menu.Init(); });
